import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { VideoHero } from '../components/sections/VideoHero';
import { HoleCard } from '../components/golf/HoleCard';
import { VideoSection } from '../components/home/VideoButton';
import { SEO } from '../components/SEO';
import { PAGE_SEO } from '../config/seoCopy';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ElCampPage = () => {
  const { t, language } = useLanguage();
  const [selectedHole, setSelectedHole] = useState(null);
  const [holes, setHoles] = useState([]);
  const [courseStats, setCourseStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [holesRes, statsRes] = await Promise.all([
          fetch(`${API_URL}/api/holes`),
          fetch(`${API_URL}/api/course-stats`)
        ]);

        if (holesRes.ok) {
          const holesData = await holesRes.json();
          setHoles(holesData);
        }

        if (statsRes.ok) {
          const statsData = await statsRes.json();
          setCourseStats(statsData);
        }
      } catch (error) {
        console.error('Failed to fetch holes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const pageContent = {
    ca: {
      intro: `<p>El camp de golf d'Aravell és un recorregut de 18 forats situat en un entorn natural privilegiat als Pirineus catalans, a la frontera amb Andorra.</p>
      <p>Amb vistes impressionants a les muntanyes i un disseny de <strong>José María Olazábal</strong> que respecta el paisatge natural, oferim una experiència de golf única que combina esport i natura.</p>
      <p>José María Olazábal és una de les grans llegendes del golf europeu i un referent absolut del golf espanyol. Bicampió del Masters d'Augusta, la seva trajectòria esportiva destaca per l'excel·lència tècnica, l'estratègia i el respecte pel joc.</p>
      <p>Com a jugador clau i posterior capità de la Ryder Cup, ha marcat una època pel seu lideratge i visió competitiva. La seva experiència al més alt nivell internacional es reflecteix en recorreguts pensats per gaudir, competir i aprendre golf en estat pur.</p>`,
      holesTitle: 'Els 18 Forats',
      holesSubtitle: 'Descobreix cada forat del nostre recorregut'
    },
    es: {
      intro: `<p>El campo de golf de Aravell es un recorrido de 18 hoyos situado en un entorno natural privilegiado en los Pirineos catalanes, en la frontera con Andorra.</p>
      <p>Con impresionantes vistas a las montañas y un diseño de <strong>José María Olazábal</strong> que respeta el paisaje natural, ofrecemos una experiencia de golf única que combina deporte y naturaleza.</p>
      <p>José María Olazábal es una de las grandes leyendas del golf europeo y un referente absoluto del golf español. Bicampeón del Masters de Augusta, su carrera deportiva se distingue por la excelencia técnica, la estrategia y el respeto por el juego.</p>
      <p>Como jugador clave y posterior capitán de la Ryder Cup, ha marcado una época por su liderazgo y visión competitiva. Su experiencia al más alto nivel internacional se refleja en diseños de recorrido pensados para disfrutar, competir y aprender golf en estado puro.</p>`,
      holesTitle: 'Los 18 Hoyos',
      holesSubtitle: 'Descubre cada hoyo de nuestro recorrido'
    },
    fr: {
      intro: `<p>Le parcours de golf d'Aravell est un terrain de 18 trous situé dans un environnement naturel privilégié au cœur des Pyrénées catalanes, à la frontière avec l'Andorre.</p>
      <p>Avec des vues spectaculaires sur les montagnes et un parcours dessiné par <strong>José María Olazábal</strong> dans le respect du paysage naturel, nous offrons une expérience de golf unique alliant sport et nature.</p>
      <p>José María Olazábal est l'une des grandes légendes du golf européen et une référence incontournable du golf espagnol. Double vainqueur du Masters d'Augusta, sa carrière se distingue par son excellence technique, son sens stratégique et son profond respect du jeu.</p>
      <p>Joueur clé puis capitaine de la Ryder Cup, il a marqué toute une époque par son leadership et sa vision compétitive. Son expérience au plus haut niveau international se reflète dans des parcours conçus pour le plaisir, la compétition et l'apprentissage du golf à l'état pur.</p>`,
      holesTitle: 'Les 18 Trous',
      holesSubtitle: 'Découvrez chaque trou de notre parcours'
    },
    en: {
      intro: `<p>Aravell Golf Course is an 18-hole layout set in a privileged natural environment in the Catalan Pyrenees, on the border with Andorra.</p>
      <p>With stunning mountain views and a course design by <strong>José María Olazábal</strong> that respects the natural landscape, we offer a unique golfing experience that blends sport and nature.</p>
      <p>José María Olazábal is one of the great legends of European golf and a true icon of Spanish golf. A two-time Masters Tournament champion, his career is defined by technical excellence, strategic intelligence, and deep respect for the game.</p>
      <p>As a key player and later Ryder Cup captain, he shaped an era through his leadership and competitive vision. His experience at the highest international level is reflected in course designs created to enjoy, compete, and learn the game of golf in its purest form.</p>`,
      holesTitle: 'The 18 Holes',
      holesSubtitle: 'Discover each hole of our course'
    }
  };

  const content = pageContent[language];

  // Course data - static information
  const courseDataContent = {
    ca: {
      title: 'Dades del Camp',
      holes: '18 Forats Par71',
      design: 'Disseny José María Olazábal',
      black: 'Negres: 6.155 m',
      white: 'Blanques: 5.872 m',
      yellow: 'Grogues: 5.337 m',
      blue: 'Blaves: 4.936 m',
      red: 'Roges: 4.634 m',
      altitude: 'Alçada: 700 m'
    },
    es: {
      title: 'Datos del Campo',
      holes: '18 Hoyos Par71',
      design: 'Diseño José María Olazábal',
      black: 'Negras: 6.155 m',
      white: 'Blancas: 5.872 m',
      yellow: 'Amarillas: 5.337 m',
      blue: 'Azules: 4.936 m',
      red: 'Rojas: 4.634 m',
      altitude: 'Altitud: 700 m'
    },
    fr: {
      title: 'Données du Parcours',
      holes: '18 Trous Par71',
      design: 'Design José María Olazábal',
      black: 'Noirs: 6.155 m',
      white: 'Blancs: 5.872 m',
      yellow: 'Jaunes: 5.337 m',
      blue: 'Bleus: 4.936 m',
      red: 'Rouges: 4.634 m',
      altitude: 'Altitude: 700 m'
    },
    en: {
      title: 'Course Data',
      holes: '18 Holes Par71',
      design: 'Design by José María Olazábal',
      black: 'Black: 6,155 m',
      white: 'White: 5,872 m',
      yellow: 'Yellow: 5,337 m',
      blue: 'Blue: 4,936 m',
      red: 'Red: 4,634 m',
      altitude: 'Altitude: 700 m'
    }
  };

  const courseData = courseDataContent[language] || courseDataContent.ca;
  const features = [
    courseData.holes,
    courseData.design,
    courseData.black,
    courseData.white,
    courseData.yellow,
    courseData.blue,
    courseData.red,
    courseData.altitude
  ];

  return (
    <main data-testid="el-camp-page">
      <SEO title={PAGE_SEO.elCamp.title} description={PAGE_SEO.elCamp.description} />
      <VideoHero
        title={t('elCamp.title')}
        subtitle={t('elCamp.subtitle')}
      />

      {/* Intro Section - 3 columns */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-6 items-center">
            {/* Left: José María Olazábal Photo */}
            <motion.div 
              className="lg:col-span-3 flex justify-center items-center"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_6551900b-6eea-49b4-a7ed-baa10c533f6e/artifacts/1peh348n_txema%20olazabal%20signature.jpg"
                alt="José María Olazábal"
                className="w-full h-auto rounded-lg shadow-md"
              />
            </motion.div>

            {/* Middle: Text content */}
            <motion.div 
              className="lg:col-span-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div 
                className="rich-text text-[#666666] text-sm lg:text-base leading-relaxed"
                dangerouslySetInnerHTML={{ __html: content.intro }}
              />
            </motion.div>

            {/* Right: Course Data */}
            <motion.div 
              className="lg:col-span-3 md:col-span-2 lg:col-span-3"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="bg-[#F9F9F7] p-5 border-l-4 border-[#1A4D2E]">
                <h3 className="font-display text-base font-medium text-[#1A4D2E] mb-3">
                  {courseData.title}
                </h3>
                <ul className="space-y-1.5">
                  {features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-xs text-[#666666]">
                      <span className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Video Section below course data */}
              <VideoSection />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Holes Section */}
      <section className="section-spacing bg-[#F9F9F7]" data-testid="holes-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#D4AF37] mb-4">
              {language === 'ca' ? 'El Recorregut' : language === 'es' ? 'El Recorrido' : language === 'fr' ? 'Le Parcours' : 'The Course'}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl text-[#1A1A1A] font-medium tracking-tight mb-4">
              {content.holesTitle}
            </h2>
            <p className="text-[#666666] max-w-2xl mx-auto">
              {content.holesSubtitle}
            </p>
          </motion.div>

          {/* Course Map */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_64ec45a5-bc37-45d2-aeb5-c91441d6b899/artifacts/ke9vl83g_UGOLF%20ARAVELL%20Mapa.png"
              alt="Mapa del Camp UGOLF ARAVELL Andorra"
              className="w-full max-w-4xl mx-auto h-auto"
              data-testid="course-map"
            />
          </motion.div>

          {/* Scorecard */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_64ec45a5-bc37-45d2-aeb5-c91441d6b899/artifacts/e348yr92_targeta%20Ugolf%20Aravell.png"
              alt="Targeta del Recorregut UGOLF ARAVELL Andorra"
              className="w-full max-w-5xl mx-auto h-auto"
              data-testid="scorecard"
            />
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {holes.map((hole, index) => (
                <motion.div
                  key={hole.number}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <HoleCard
                    hole={{
                      number: hole.number,
                      par: hole.par,
                      distance: hole.distance_meters,
                      hcp: hole.handicap,
                      description: hole.description?.[language] || hole.description?.ca || '',
                      image: `${API_URL}/api/uploads/${hole.number}.png`
                    }}
                    language={language}
                    onClick={() => setSelectedHole({
                      ...hole,
                      image: `${API_URL}/api/uploads/${hole.number}.png`
                    })}
                  />
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Hole Image Popup - simple lightbox */}
      {selectedHole && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 cursor-pointer"
          onClick={() => setSelectedHole(null)}
          data-testid="hole-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={() => setSelectedHole(null)}
            aria-label="Tancar"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={selectedHole.image || `https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?q=80&w=800`}
            alt={`Forat ${selectedHole.number}`}
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </main>
  );
};

export default ElCampPage;
